import { Relation, ShortcutActionType, ShortcutMatchType } from '@intelligence/components/GenreSelect/enums';

import type {
  IGenreSourceOption,
  IGenreSource,
  IGenreSourcesShortcuts,
  IGenreSourcesRelationable,
  IGenreSources,
} from '@intelligence/components/GenreSelect/types';

import ActionApnnieIcon from '@intelligence/assets/svg/dataSource/action-apnnie.svg';
import ActionthemeIcon from '@intelligence/assets/svg/dataSource/action-theme.svg';
import ThemeNochangeIcon from '@intelligence/assets/svg/dataSource/theme-nochange.svg';
import ActionNochangeIcon from '@intelligence/assets/svg/dataSource/appnnie.svg';
import WeixinIcon from '@intelligence/assets/imgs/casualDailyReports/icon-weixin.png';
import WeixinIconAction from '@intelligence/assets/imgs/casualDailyReports/icon-weixin.png';
import SensorTowerIcon from '@intelligence/assets/svg/dataSource/sensor-tower.svg';
import SensorTowerActivatedIcon from '@intelligence/assets/svg/dataSource/sensor-tower-activated.svg';
import AppMagicIcon from '@intelligence/assets/imgs/dataSource/app-magic.png';
import AppMagicActivatedIcon from '@intelligence/assets/imgs/dataSource/app-magic-activated.png';
import RobloxIcon from '@intelligence/assets/svg/icon-roblox.svg';

// 品类数据源键常量
export const GENRE_SOURCE_MAP = {
  IEGG: 'iegg',
  SENSORTOWER: 'sensortower',
  APP_MAGIC: 'appmagic',
  APP_ANNIE: 'annie',
  MINIGAME: 'wechat_minigame',
  ROBLOX: 'roblox',
};

// relation为and时，单个主品类下最多勾选的副品类的数量，副品类勾选禁用、切换主品类relation、全选副品类、勾选主品类等操作 都会根据这个配置 做对应的限制 和 调整
export const MAX_SUB_GENRES_OF_RELATION_AND = 2;
export const MAX_SUB_GENRES_SELECT_MSG = 'You can only select up to 2 options';
export const MAX_SUB_GENRES_SELECT_MSG_CN = 'You can only select up to 2 options';

// 快捷勾选按钮配置
export const SHORTCUTS: IGenreSourcesShortcuts = {
  [GENRE_SOURCE_MAP.IEGG]: [
    {
      type: ShortcutActionType.Compose,
      text: 'SOC',
      textCN: 'SOC',
      value: 'Open World Survival Craft',
      bubbleText: 'SOC: Survival, Open-World, Crafting',
      bubbleTextCN: 'SOC: 开放世界生存建造',
    },
    {
      type: ShortcutActionType.Compose,
      text: 'BR',
      textCN: 'BR',
      value: 'Battle Royale',
      bubbleText: 'BR: Battle Royale',
      bubbleTextCN: 'BR: 大逃杀',
    },
    {
      type: ShortcutActionType.Compose,
      text: 'Tarkov-Like',
      textCN: 'Tarkov-Like',
      value: 'Tarkov-Like',
      bubbleText: 'Tarkov-Like',
      bubbleTextCN: '类逃离塔可夫',
    },
  ],
  [GENRE_SOURCE_MAP.SENSORTOWER]: [],
  [GENRE_SOURCE_MAP.APP_MAGIC]: [
    {
      type: ShortcutActionType.Replace,
      text: 'Mahjong',
      textCN: '麻将',
      value: 'Mahjong',
      bubbleText: 'Mahjong',
      bubbleTextCN: '麻将',
      matchType: ShortcutMatchType.Includes,
    },
    {
      type: ShortcutActionType.Compose,
      text: 'Narrative Merge-2',
      textCN: '剧情二合',
      value: 'Merge-2: Complex Metagame',
      bubbleText: 'Narrative Merge-2',
      bubbleTextCN: '剧情二合',
      matchType: ShortcutMatchType.Exact,
    },
  ],
  [GENRE_SOURCE_MAP.APP_ANNIE]: [],
};

// 是否开启relation勾选交互
export const RELATIONABLE: IGenreSourcesRelationable = {
  [GENRE_SOURCE_MAP.IEGG]: true,
  [GENRE_SOURCE_MAP.SENSORTOWER]: false,
  [GENRE_SOURCE_MAP.APP_MAGIC]: false,
  [GENRE_SOURCE_MAP.APP_ANNIE]: false,
  [GENRE_SOURCE_MAP.ROBLOX]: true,
};

export const GENRE_SOURCES: IGenreSources = [GENRE_SOURCE_MAP.IEGG, GENRE_SOURCE_MAP.APP_ANNIE];

export const GENRE_SOURCES_NEW: IGenreSources = [
  GENRE_SOURCE_MAP.IEGG,
  GENRE_SOURCE_MAP.SENSORTOWER,
  GENRE_SOURCE_MAP.APP_ANNIE,
];

export const GENRE_SOURCES_MULTI_DATASOURCE_TOPCHARTS_PAGE: IGenreSources = [
  GENRE_SOURCE_MAP.IEGG,
  GENRE_SOURCE_MAP.SENSORTOWER,
  GENRE_SOURCE_MAP.APP_MAGIC,
  GENRE_SOURCE_MAP.APP_ANNIE,
];

export const GENRE_SOURCES_METADATA: {
  [key in IGenreSource]: IGenreSourceOption;
} = {
  [GENRE_SOURCE_MAP.IEGG]: {
    value: GENRE_SOURCE_MAP.IEGG,
    text: 'IEGG',
    textCN: 'IEGG',
    icon: ThemeNochangeIcon,
    actionIcon: ActionthemeIcon,
    remarks:
      "The IEGG genre tags for mobile games is the result of a comprehensive review from Game Research Center experts with their database. The IEGG genre tags for PC games are generated with genre sets provided by Game Research Center reviewers based on Steam Tags, while the genre tags for Console games are mapped from the genres given to the PC versions of the same game. Otherwise, use Riot Pixels as references for games which aren't published on Steam.",
    remarksCN:
      '手游的 IEGG品类由游戏研究中心专家基于数据库数据综合评估后提供。端游IEGG品类生成自游戏研究中心专家根据 Steam 标签提供的品类体系，而主机游戏品类则从同一游戏的 PC 版本所赋予的类型映射而来。此外，对于未在 Steam 上发布的游戏，使用 Riot Pixels 提供的类型作为参考。',
  },
  [GENRE_SOURCE_MAP.SENSORTOWER]: {
    value: GENRE_SOURCE_MAP.SENSORTOWER,
    text: 'Sensor Tower',
    textCN: 'Sensor Tower',
    icon: SensorTowerIcon,
    actionIcon: SensorTowerActivatedIcon,
    remarks:
      "Sensor Tower's Genre comes from its Game IQ module. Genre and Sub-genre are mostly focused on core gameplay mechanics. So the Cards Sub-genre (for example, UNO) lives in the Lifestyle & Puzzle Class, but poker and other card games with gambling functionality live in the Casino category.",
    remarksCN:
      '该品类体系来自 Sensor Tower 的 Game IQ 模块，主要基于核心玩法进行游戏品类与子品类的划分。例如：卡牌游戏（如 UNO）会被归入“休闲与解谜”大类之下；而扑克及其他具备博彩功能的卡牌游戏，则被归入“赌场”类别。',
  },
  [GENRE_SOURCE_MAP.APP_MAGIC]: {
    value: GENRE_SOURCE_MAP.APP_MAGIC,
    text: 'App Magic',
    textCN: 'App Magic',
    icon: AppMagicIcon,
    actionIcon: AppMagicActivatedIcon,
    remarks: '',
    remarksCN: '',
  },
  [GENRE_SOURCE_MAP.APP_ANNIE]: {
    value: GENRE_SOURCE_MAP.APP_ANNIE,
    text: 'data.ai',
    textCN: 'data.ai',
    icon: ActionNochangeIcon,
    actionIcon: ActionApnnieIcon,
    remarks:
      'Since data.ai was acquired by Sensor Tower, it will no longer be possible to update the data.ai genra tags for new games.',
    remarksCN: '由于data.ai被Sensor Tower收购，之后将无法更新新游的data.ai品类标签。',
  },
  [GENRE_SOURCE_MAP.MINIGAME]: {
    value: GENRE_SOURCE_MAP.MINIGAME,
    text: 'WeChat',
    textCN: 'WeChat',
    icon: WeixinIcon,
    actionIcon: WeixinIconAction,
    remarks: 'Data of Genre comes from the WeChat Mini Game leaderboard on the Gravity Engine.',
    remarksCN: '品类数据来源于第三方平台引力引擎的微信小游戏榜单。',
  },
  [GENRE_SOURCE_MAP.ROBLOX]: {
    value: GENRE_SOURCE_MAP.ROBLOX,
    text: 'Roblox',
    textCN: 'Roblox',
    icon: RobloxIcon,
    actionIcon: RobloxIcon,
    remarks: '',
    remarksCN: '',
  },
};

export const RELATION_JOINERS: {
  [key in Relation]: string;
} = {
  [Relation.And]: ' + ',
  [Relation.Or]: ', ',
};
